/**
 * THE ZELLE CODE, from `pay.zelle.qr_url`, in the one function that reads it.
 *
 * Ops's contract says `qr_url` is the address the code encodes, not an image: `ZELLE_QR_URL`, an
 * `https://enroll.zellepay.com/qr-codes/?data=` address, which home draws at any size, as ops's own
 * page draws it with an encoder bundled into the page, error correction M and a quiet zone of four
 * modules; ops stores and serves no image (`docs/seams/home.md` at 0b51e49, section 5, Q4). So the
 * code is drawn here, crisp at any size, from the matrix `qrcode-generator` (2.0.4, pinned exactly,
 * no dependencies of its own) gives for it, and "Open Zelle's page" is a plain link to the same
 * address (`Zelle.tsx`).
 *
 * - The address is encoded as the browser writes it (`URL.href`), which is always ASCII, so the
 *   encoder's byte mode, which keeps only the low byte of each character, never cuts one.
 * - Nothing is drawn for what is not an https address, or is too long for any code: the screen
 *   then gives the tag alone.
 * - The code is hidden from a screen reader, which cannot scan it, and is given the same thing in
 *   words: the tag beside it and "Open Zelle's page".
 * - Dark modules on a white ground with a quiet zone of four modules around them, which a scanner
 *   needs, in the website's own ink and surface.
 */
import qrcode from 'qrcode-generator';
import type { ReactElement } from 'react';

/** The quiet zone around the code, in modules. */
const QUIET = 4;

/** The dark modules of the code for an address, as one path, and its size with the quiet zone. */
function modulesOf(address: string): { readonly size: number; readonly d: string } | null {
  let href: string;
  try {
    const url = new URL(address);
    if (url.protocol !== 'https:') return null;
    href = url.href;
  } catch {
    return null;
  }
  try {
    const qr = qrcode(0, 'M');
    qr.addData(href, 'Byte');
    qr.make();
    const count = qr.getModuleCount();
    let d = '';
    for (let row = 0; row < count; row += 1) {
      for (let col = 0; col < count; col += 1) {
        if (qr.isDark(row, col)) d += `M${col + QUIET} ${row + QUIET}h1v1h-1z`;
      }
    }
    return { size: count + 2 * QUIET, d };
  } catch {
    // Too long for any code.
    return null;
  }
}

/**
 * The code for the address ops pushes, at 200 pixels square (12.5rem, so it grows with her text
 * size), or null when there is none to draw.
 */
export function drawZelleCode(address: string): ReactElement | null {
  const code = modulesOf(address);
  if (code === null) return null;
  const { size, d } = code;
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
      className="block size-50"
    >
      <rect width={size} height={size} className="fill-surface-raised" />
      <path d={d} className="fill-text-body" />
    </svg>
  );
}
